import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { Modal } from '@mui/material'
import Button from '../Button/Button'
import Calendar from '../Calendar/Calendar'
import { createAlert, updateAlertById } from '../../features/alerts/alerts'
import { showSnackBar } from '../Snackbar/Snackbar'
import { useMyContextLoading } from '../LoadingContextProvider/LoadingContextProvider'
import { dateToServer, serverToLocal } from '../../utils/date'

const AddAlertModal = ({ userId, editingAlert, onClose, onSaved, externalOpen = false, hideButton = false }) => {
  const dispatch = useDispatch()
  const { triggerLoading } = useMyContextLoading()
  const [internalOpen, setInternalOpen] = useState(false)

  // Use external control if provided, otherwise use internal state
  const open = externalOpen || internalOpen
  const setOpen = (value) => {
    setInternalOpen(value)
    if (!value && onClose) onClose()
  }
  const [message, setMessage] = useState('')
  const [type, setType] = useState('info')
  const [expiresAt, setExpiresAt] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isEditing = !!editingAlert

  // Open modal when editingAlert changes
  useEffect(() => {
    if (editingAlert) {
      setOpen(true)
      setMessage(editingAlert.message)
      setType(editingAlert.type)
      setExpiresAt(editingAlert.expiresAt ? serverToLocal(editingAlert.expiresAt) : null)
    }
  }, [editingAlert])

  const resetForm = () => {
    setMessage('')
    setType('info')
    setExpiresAt(null)
  }

  const handleOpen = () => {
    resetForm()
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    resetForm()
    if (onClose) onClose()
  }

  const handleSubmit = async () => {
    if (!message.trim()) {
      showSnackBar('error', 'Message is required')
      return
    }

    setIsSubmitting(true)
    triggerLoading(true)

    try {
      let response
      if (isEditing) {
        response = await dispatch(updateAlertById({
          alertId: editingAlert._id,
          message,
          type,
          expiresAt: expiresAt ? dateToServer(expiresAt) : null
        }))
      } else {
        response = await dispatch(createAlert({
          userId,
          message,
          type,
          expiresAt: expiresAt ? dateToServer(expiresAt) : null
        }))
      }

      if (response.payload?.data) {
        showSnackBar('success', isEditing ? 'Alert updated successfully' : 'Alert created successfully')
        handleClose()
        if (onSaved) onSaved()
      } else {
        showSnackBar('error', response.payload || 'Failed to save alert')
      }
    } catch (error) {
      showSnackBar('error', 'An error occurred')
    } finally {
      triggerLoading(false)
      setIsSubmitting(false)
    }
  }

  const getTypeButtonClasses = (alertType) => {
    const isSelected = type === alertType
    const baseClasses = 'px-4 py-2 rounded-lg border capitalize transition-colors'

    if (isSelected) {
      switch (alertType) {
        case 'info':
          return `${baseClasses} bg-info-light border-info-main text-info-main`
        case 'warning':
          return `${baseClasses} bg-warning-light border-warning-main text-warning-main`
        case 'critical':
          return `${baseClasses} bg-error-light border-error-main text-error-main`
        default:
          return `${baseClasses} bg-divider border-divider text-text-secondary`
      }
    }
    return `${baseClasses} bg-paper border-divider text-text-secondary hover:bg-divider/50`
  }

  return (
    <>
      {/* Add Alert Button - hidden when controlled externally */}
      {!hideButton && (
        <button
          onClick={handleOpen}
          className="mt-2 flex items-center gap-2 text-sm text-primary-main hover:text-primary-main/80 font-medium"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Alert
        </button>
      )}

      <Modal open={open} onClose={handleClose} closeAfterTransition>
        <div className="flex items-center justify-center h-screen">
          <div className="bg-surface rounded-2xl min-w-[500px] max-h-content">
            {/* Header */}
            <div className="px-6 pt-6">
              <div className="mb-6 flex justify-between">
                <h4 className="font-sans text-lg-18 text-text-primary">
                  {isEditing ? 'Edit Alert' : 'Add New Alert'}
                </h4>
                <button onClick={handleClose} className="cursor-pointer">
                  <svg width={24} height={25} viewBox="0 0 24 25" fill="none">
                    <path
                      d="M13.4032 12.4942L17.6976 8.20953C18.0892 7.8179 18.0892 7.18292 17.6976 6.79129C17.306 6.39965 16.671 6.39965 16.2794 6.79129L11.995 11.086L7.71063 6.79129C7.31902 6.39965 6.68409 6.39965 6.29248 6.79129C5.90087 7.18292 5.90087 7.8179 6.29248 8.20953L10.5869 12.4942L6.29248 16.7789C6.10342 16.9665 5.99707 17.2218 5.99707 17.4881C5.99707 17.7544 6.10342 18.0096 6.29248 18.1972C6.48 18.3863 6.73527 18.4926 7.00155 18.4926C7.26784 18.4926 7.52311 18.3863 7.71063 18.1972L11.995 13.9025L16.2794 18.1972C16.4669 18.3863 16.7222 18.4926 16.9885 18.4926C17.2548 18.4926 17.51 18.3863 17.6976 18.1972C17.8866 18.0096 17.993 17.7544 17.993 17.4881C17.993 17.2218 17.8866 16.9665 17.6976 16.7789L13.4032 12.4942Z"
                      fill="var(--color-gray-600)"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <div className="flex-shrink-0 w-full h-px bg-divider" />

            {/* Form */}
            <div className="px-6 py-6">
              {/* Alert Type */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Alert Type
                </label>
                <div className="flex gap-3">
                  {['info', 'warning', 'critical'].map((alertType) => (
                    <button
                      key={alertType}
                      type="button"
                      onClick={() => setType(alertType)}
                      className={getTypeButtonClasses(alertType)}
                    >
                      {alertType}
                    </button>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Message *
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Enter alert message..."
                  className="w-full px-3 py-2 border border-divider rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary-main bg-paper text-text-primary"
                  rows={3}
                />
              </div>

              {/* Expiration Date */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Expiration Date (optional)
                </label>
                <Calendar
                  value={expiresAt}
                  onChange={(newValue) => setExpiresAt(newValue)}
                  disablePast={true}
                  withLabel={false}
                  height="42px"
                  parentClassName="w-full"
                  optional={true}
                />
                <p className="mt-1 text-xs text-text-secondary">
                  Alert will automatically hide after this date
                </p>
              </div>
            </div>

            <div className="flex-shrink-0 w-full h-px bg-divider" />

            {/* Footer */}
            <div className="px-6 py-4 flex justify-end gap-4">
              <Button onClick={handleClose} type="outlined" width="w-[120px]">
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting || !message.trim()}
                width="w-[120px]"
              >
                {isSubmitting ? 'Saving...' : isEditing ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  )
}

AddAlertModal.propTypes = {
  userId: PropTypes.string.isRequired,
  editingAlert: PropTypes.shape({
    _id: PropTypes.string,
    message: PropTypes.string,
    type: PropTypes.string,
    expiresAt: PropTypes.string,
  }),
  onClose: PropTypes.func,
  onSaved: PropTypes.func,
  externalOpen: PropTypes.bool,
  hideButton: PropTypes.bool,
}

AddAlertModal.defaultProps = {
  editingAlert: null,
  onClose: () => {},
  onSaved: () => {},
  externalOpen: false,
  hideButton: false,
}

export default AddAlertModal
